import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Step,
  StepIconProps,
  StepLabel,
  Stepper,
  styled,
  Typography,
  StepConnector,
  stepConnectorClasses,
  Box,
} from "@mui/material";
import { MandatorContext } from "@rentablo/theming";
import Document from "./icons/document.svg?react";
import Mail from "./icons/mail.svg?react";
import Printer from "./icons/printer.svg?react";

const StepperStyled = styled(Stepper)(({ theme }) => ({
  background: "transparent",
  margin: theme.spacing(0, -2, 11),

  [theme.breakpoints.down(730)]: {
    margin: theme.spacing(0, 0, 9),
    padding: 0,
    display: "block",

    // this is to get rid of useMediaQuery
    "& .MuiStepConnector-root": {
      padding: "0 32px",
      margin: "12px 0",
    },

    "& .MuiStep-root": {
      padding: "8px 0",
    },

    "& .MuiStepConnector-line": {
      borderLeftStyle: "solid",
      borderTopStyle: "none",
      minHeight: "24px",
    },
  },
}));

const StepLabelContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

const StepIconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "78px",
  height: "78px",
  boxShadow: theme.type === "onvista" ? "none" : theme.shadows[2],
  borderColor: theme.palette.border.primary,
  borderWidth: theme.type === "onvista" ? "2px" : "1px",
  borderStyle: "solid",
  borderRadius: "50%",
  color:
    theme.type === "onvista"
      ? theme.palette.primary.main
      : theme.palette.primary.dark,

  [theme.breakpoints.down("md")]: {
    width: 64,
    height: 64,
    "& > svg": {
      fontSize: 36,
    },
  },
}));

const getStepIcon = (props: StepIconProps) => {
  const icons: { [index: string]: React.ReactElement } = {
    1: <Document width="44px" height="44px" />,
    2: <Printer width="44px" height="44px" />,
    3: <Mail width="44px" height="44px" />,
  };

  return <StepIconContainer>{icons[String(props.icon)]}</StepIconContainer>;
};

const Connector = styled(StepConnector)(({ theme }) => ({
  padding: theme.spacing(0, 8),
  margin: theme.spacing(3, 0),

  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: theme.type === "onvista" ? 2 : 1,
    borderTopColor: theme.palette.border.primary,
  },
}));

export const CashbackStepper = () => {
  const { t } = useTranslation();
  const { isOnvista } = useContext(MandatorContext);

  return (
    <StepperStyled activeStep={3} connector={<Connector />}>
      <Step>
        <StepLabel StepIconComponent={getStepIcon}>
          <StepLabelContainer>
            <Typography
              variant={isOnvista ? "subtitle2" : "body2"}
              color="textSecondary"
            >
              {t("cashbackWizard.start.steps.first.title")}
            </Typography>
            <Typography
              variant={isOnvista ? "body1" : "body2"}
              color="textPrimary"
            >
              {t("cashbackWizard.start.steps.first.description")}
            </Typography>
          </StepLabelContainer>
        </StepLabel>
      </Step>
      <Step>
        <StepLabel StepIconComponent={getStepIcon}>
          <StepLabelContainer>
            <Typography
              variant={isOnvista ? "subtitle2" : "body2"}
              color="textSecondary"
            >
              {t("cashbackWizard.start.steps.second.title")}
            </Typography>
            <Typography
              variant={isOnvista ? "body1" : "body2"}
              color="textPrimary"
            >
              {t("cashbackWizard.start.steps.second.description")}
            </Typography>
          </StepLabelContainer>
        </StepLabel>
      </Step>
      <Step>
        <StepLabel StepIconComponent={getStepIcon}>
          <StepLabelContainer>
            <Typography
              variant={isOnvista ? "subtitle2" : "body2"}
              color="textSecondary"
            >
              {t("cashbackWizard.start.steps.third.title")}
            </Typography>
            <Typography
              variant={isOnvista ? "body1" : "body2"}
              color="textPrimary"
            >
              {t("cashbackWizard.start.steps.third.description")}
            </Typography>
          </StepLabelContainer>
        </StepLabel>
      </Step>
    </StepperStyled>
  );
};
